import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Play, Pause, Sparkle, Microphone } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

/**
 * VoiceClipPicker — bottom sheet listing the user's AI voice clips and Voice
 * Studio recordings. onPick({ path, url, duration, title }).
 */
export default function VoiceClipPicker({ open, onClose, onPick, inline = false }) {
  const [tab, setTab] = useState("ai");
  const [ai, setAi] = useState(null);
  const [rec, setRec] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/ai/voice/audio", { params: { limit: 50 } }).then((r) => setAi(r.data)).catch(() => setAi([]));
    api.get("/voice/me", { params: { limit: 50 } }).then((r) => setRec(r.data)).catch(() => setRec([]));
    return () => audioRef.current?.pause();
  }, [open]);

  if (!open) return null;

  const play = (key, url) => {
    if (playing === key) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const a = new Audio(fileUrl(url));
    a.onended = () => setPlaying(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlaying(key);
  };

  const items = tab === "ai"
    ? (ai || []).map((a) => ({ key: a.id, path: a.storage_path, url: a.url, duration: a.duration_sec, title: a.title, sub: `${a.voice_name} · ${a.kind}` }))
    : (rec || []).map((r) => ({ key: r.id, path: r.storage_path, url: `/api/files/${r.storage_path}`, duration: r.duration_sec, title: r.title || "Recording", sub: r.kind }));
  const loading = tab === "ai" ? ai === null : rec === null;

  const body = (
    <>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setTab("ai")} data-testid="voice-picker-tab-ai" className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border ${tab === "ai" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>AI clips</button>
          <button onClick={() => setTab("rec")} data-testid="voice-picker-tab-rec" className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border ${tab === "rec" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>Voice Studio</button>
        </div>
        {loading ? <p className="text-cream/40 text-sm">Loading…</p> : items.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-cream/60 text-sm">No clips yet.</p>
            <Link to={tab === "ai" ? "/ai-voice" : "/voice"} className="inline-flex items-center gap-1 mt-3 px-4 py-2 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase">
              <Sparkle size={12} weight="fill" /> {tab === "ai" ? "Make an AI clip" : "Record one"}
            </Link>
          </div>
        ) : (
          <div className="space-y-2" data-testid="voice-picker-list">
            {items.map((it) => (
              <div key={it.key} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5" data-testid={`voice-picker-item-${it.key}`}>
                <button onClick={() => play(it.key, it.url)} className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${playing === it.key ? "bg-papaya text-ink" : "bg-white/10 text-cream"}`}>
                  {playing === it.key ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-bold text-sm truncate">{it.title}</p>
                  <p className="text-cream/50 text-[11px] truncate">{it.sub}{it.duration ? ` · ${Math.round(it.duration)}s` : ""}</p>
                </div>
                <button onClick={() => { audioRef.current?.pause(); onPick(it); }} data-testid={`voice-picker-send-${it.key}`} className="px-3 py-1.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase">Send</button>
              </div>
            ))}
          </div>
        )}
    </>
  );

  if (inline) return <div data-testid="voice-clip-picker">{body}</div>;
  return (
    <div className="fixed inset-0 z-[125] flex items-end" data-testid="voice-clip-picker">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full bg-ink rounded-t-3xl border-t border-white/10 p-5 max-h-[75vh] overflow-y-auto animate-fade-up">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-cream text-xl font-black tracking-tight flex items-center gap-2"><Microphone size={20} weight="fill" className="text-papaya" /> Send a voice clip</h3>
          <button onClick={onClose} data-testid="voice-picker-close" className="p-2 rounded-full hover:bg-white/5"><X size={20} className="text-cream" /></button>
        </div>
        {body}
      </div>
    </div>
  );
}
